import { Box, type SxProps, type Theme, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MOTION, NEUTRAL, SURFACE } from "../../../theme/tokens";

// Strip everything that isn't a digit.
const NON_DIGIT = /\D/g;

export interface SystemCodeInputProps {
	/** Number of code cells. Defaults to 6. */
	length?: number;
	/** Controlled value — a digit string up to `length`. */
	value: string;
	/** Fires with the sanitized (digits-only, capped) value. */
	onChange: (value: string) => void;
	/** Fires once all cells are filled (e.g. to auto-submit). */
	onComplete?: (value: string) => void;
	disabled?: boolean;
	/** Focus the first cell on mount. */
	autoFocus?: boolean;
	/** Render cells in the error color. */
	error?: boolean;
	/** Name of the hidden input, so the code is in FormData and is the autofill target. */
	name?: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
}

/**
 * Segmented N-cell code entry: N boxed cells backed by one value.
 *
 * There are two inputs: the visible cells (controlled, own the keyboard behavior) and one
 * hidden input that carries `name`/`autocomplete="one-time-code"` for FormData + SMS autofill.
 * `useWebOtpAutofill` fills the hidden input and fires a native `input` event; we spread those
 * digits across the cells. The forwarded ref points at the hidden input.
 */
export const SystemCodeInput = forwardRef<HTMLInputElement, SystemCodeInputProps>(function SystemCodeInput(
	{ length = 6, value, onChange, onComplete, disabled = false, autoFocus = false, error = false, name, dataTestId, sx },
	forwardedRef,
) {
	const theme = useTheme();
	const { t } = useTranslation();

	const cellRefs = useRef<Array<HTMLInputElement | null>>([]);
	const hiddenInputRef = useRef<HTMLInputElement | null>(null);

	// Sanitize + cap, then notify. Returns the sanitized value for callers that need it.
	const commit = useCallback(
		(next: string) => {
			const sanitized = next.replace(NON_DIGIT, "").slice(0, length);
			if (sanitized !== value) {
				onChange(sanitized);
			}
			if (sanitized.length === length) {
				onComplete?.(sanitized);
			}
			return sanitized;
		},
		[length, value, onChange, onComplete],
	);

	const focusCell = useCallback((index: number) => {
		const clamped = Math.max(0, Math.min(index, cellRefs.current.length - 1));
		cellRefs.current[clamped]?.focus();
		cellRefs.current[clamped]?.select();
	}, []);

	// Keep the hidden input's DOM value matching `value` so FormData is always correct.
	useEffect(() => {
		if (hiddenInputRef.current && hiddenInputRef.current.value !== value) {
			hiddenInputRef.current.value = value;
		}
	}, [value]);

	// Autofill / paste into the hidden input arrives as a native `input` event — spread it out.
	useEffect(() => {
		const input = hiddenInputRef.current;
		if (!input) {
			return;
		}
		const onNativeInput = () => {
			const sanitized = commit(input.value);
			focusCell(sanitized.length);
		};
		input.addEventListener("input", onNativeInput);
		return () => input.removeEventListener("input", onNativeInput);
	}, [commit, focusCell]);

	useEffect(() => {
		if (autoFocus && !disabled) {
			focusCell(0);
		}
	}, [autoFocus, disabled, focusCell]);

	const cells = useMemo(() => Array.from({ length }, (_, i) => i), [length]);

	const handleCellChange = useCallback(
		(index: number, raw: string) => {
			const digits = raw.replace(NON_DIGIT, "");
			if (!digits) {
				return;
			}
			// Fill from this cell onward (handles a multi-digit paste into one cell).
			const chars = value.split("");
			for (let offset = 0; offset < digits.length && index + offset < length; offset++) {
				chars[index + offset] = digits[offset];
			}
			commit(chars.join(""));
			focusCell(index + digits.length);
		},
		[value, length, commit, focusCell],
	);

	const handleKeyDown = useCallback(
		(index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === "Backspace") {
				event.preventDefault();
				const chars = value.split("");
				if (chars[index]) {
					// Clear this cell.
					chars[index] = "";
					commit(chars.join(""));
				} else if (index > 0) {
					// Already empty — step back and clear the previous cell.
					chars[index - 1] = "";
					commit(chars.join(""));
					focusCell(index - 1);
				}
			} else if (event.key === "ArrowLeft") {
				event.preventDefault();
				focusCell(index - 1);
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				focusCell(index + 1);
			}
		},
		[value, commit, focusCell],
	);

	const handlePaste = useCallback(
		(event: React.ClipboardEvent<HTMLInputElement>) => {
			event.preventDefault();
			const next = commit(event.clipboardData.getData("text"));
			focusCell(next.length);
		},
		[commit, focusCell],
	);

	const setHiddenRef = useCallback(
		(node: HTMLInputElement | null) => {
			hiddenInputRef.current = node;
			if (typeof forwardedRef === "function") {
				forwardedRef(node);
			} else if (forwardedRef) {
				forwardedRef.current = node;
			}
		},
		[forwardedRef],
	);

	return (
		<Box
			data-testid={dataTestId}
			// A numeric code always reads left-to-right, even in RTL locales.
			dir="ltr"
			sx={{
				position: "relative",
				display: "flex",
				justifyContent: "center",
				gap: { xs: "0.4rem", sm: "0.6rem" },
				width: "100%",
				...sx,
			}}
		>
			{/* Hidden input: the ref target, FormData carrier, and SMS-autofill sink. */}
			<Box
				component="input"
				ref={setHiddenRef}
				type="text"
				name={name}
				defaultValue={value}
				autoComplete="one-time-code"
				inputMode="numeric"
				tabIndex={-1}
				aria-hidden="true"
				disabled={disabled}
				data-testid={dataTestId ? `${dataTestId}-hidden` : undefined}
				sx={{
					position: "absolute",
					width: 1,
					height: 1,
					padding: 0,
					margin: "-1px",
					border: 0,
					opacity: 0,
					insetInlineStart: 0,
					top: 0,
					pointerEvents: "none",
				}}
			/>
			{cells.map((index) => {
				const isActive = index === value.length && value.length < length;
				const filled = Boolean(value[index]);
				const borderColor = error ? theme.palette.error.main : isActive ? theme.palette.primary.main : filled ? NEUTRAL[400] : NEUTRAL[300];
				return (
					<Box
						key={index}
						component="input"
						ref={(node: HTMLInputElement | null) => {
							cellRefs.current[index] = node;
						}}
						type="text"
						inputMode="numeric"
						autoComplete="off"
						aria-label={t("otp.code.digit.aria", { index: index + 1, length })}
						value={value[index] ?? ""}
						disabled={disabled}
						maxLength={1}
						data-testid={dataTestId ? `${dataTestId}-cell-${index}` : undefined}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCellChange(index, e.target.value)}
						onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
						onPaste={handlePaste}
						onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
						sx={{
							width: { xs: "2.5rem", sm: "3rem" },
							height: { xs: "3rem", sm: "3.5rem" },
							minWidth: 0,
							flex: "0 1 auto",
							textAlign: "center",
							fontSize: { xs: "1.25rem", sm: "1.5rem" },
							fontWeight: 600,
							color: theme.palette.text.primary,
							backgroundColor: disabled ? NEUTRAL[100] : SURFACE,
							border: "1px solid",
							borderColor,
							borderRadius: `${theme.shape.borderRadius}px`,
							outline: "none",
							caretColor: theme.palette.primary.main,
							boxShadow: isActive ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}` : "none",
							transition: `border-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, box-shadow ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
							"@media (prefers-reduced-motion: reduce)": { transition: "none" },
							"&:focus": {
								borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
								boxShadow: `0 0 0 2px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.2)}`,
							},
						}}
					/>
				);
			})}
		</Box>
	);
});
